/**
 * KpiTile — one of the 4 hero metrics on the Quality Dashboard. Shows
 * current value, traffic-light band, delta vs prior period, and a
 * 12-point sparkline. Accessible: the whole tile is a button so
 * keyboard users get tab focus + Enter to drill in. Band colour is
 * surfaced both visually and inside aria-label (WCAG 1.4.1).
 */

import { useMemo } from 'react';
import { MiniSparkline } from './MiniSparkline';
import type { KpiBand, KpiCell } from './useQualityDashboard';

const BAND_STYLES: Record<KpiBand, { dot: string; sparkline: string; pill: { bg: string; fg: string } }> = {
  green: {
    dot: 'var(--color-status-success, #15803D)',
    sparkline: 'var(--color-status-success, #15803D)',
    pill: { bg: 'var(--color-status-success-bg, #ECFDF5)', fg: 'var(--color-status-success, #15803D)' },
  },
  amber: {
    dot: 'var(--color-status-warning, #B45309)',
    sparkline: 'var(--color-status-warning, #B45309)',
    pill: { bg: 'var(--color-status-warning-bg, #FFFBEB)', fg: 'var(--color-status-warning, #B45309)' },
  },
  red: {
    dot: 'var(--color-status-error, #B91C1C)',
    sparkline: 'var(--color-status-error, #B91C1C)',
    pill: { bg: 'var(--color-status-error-bg, #FEF2F2)', fg: 'var(--color-status-error, #B91C1C)' },
  },
};

export interface KpiTileProps {
  cell: KpiCell;
  onOpen?: () => void;
}

export function KpiTile({ cell, onOpen }: KpiTileProps) {
  const style = BAND_STYLES[cell.band];
  const valueDisplay = useMemo(() => {
    if (cell.value === null) return '—';
    if (cell.unit === 'percent') return `${cell.value}%`;
    if (cell.unit === 'days') return `${cell.value}d`;
    return String(cell.value);
  }, [cell.value, cell.unit]);
  const delta = cell.delta_pct_vs_prior_period;
  const deltaSign = delta > 0 ? '+' : '';
  const deltaUnit = cell.unit === 'percent' ? 'pp' : cell.unit === 'days' ? 'd' : '';
  const deltaIsGood = useMemo(() => {
    // For on-time-percent: higher is better. For cycle / turnaround / breach: lower is better.
    const lowerIsBetter = cell.unit === 'days' || cell.label.toLowerCase().includes('breach');
    return lowerIsBetter ? delta <= 0 : delta >= 0;
  }, [cell.label, cell.unit, delta]);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${cell.label}, ${valueDisplay}, band ${cell.band}, change ${deltaSign}${delta}${deltaUnit} vs prior period`}
      style={{
        textAlign: 'start',
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--color-border-subtle, #E5E7EB)',
        background: 'var(--color-bg-surface-elevated, #FFFFFF)',
        cursor: onOpen ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span aria-hidden style={{ width: 8, height: 8, borderRadius: 9999, background: style.dot, flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #4B5563)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cell.label}
        </span>
      </header>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, color: 'var(--color-text-primary, #111827)' }}>
          {valueDisplay}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999,
          background: deltaIsGood ? 'var(--color-status-success-bg, #ECFDF5)' : 'var(--color-status-error-bg, #FEF2F2)',
          color: deltaIsGood ? 'var(--color-status-success, #15803D)' : 'var(--color-status-error, #B91C1C)',
        }}>
          {deltaSign}{delta}{deltaUnit}
        </span>
      </div>
      <div style={{ color: style.sparkline, marginTop: 4 }}>
        <MiniSparkline points={cell.history} stroke={style.sparkline} />
      </div>
    </button>
  );
}
